import { createSlice } from '@reduxjs/toolkit';

/**
 * Initial state for workspace/API builder
 */
const initialState = {
  activeTab: 'API', // 'API' | 'Orchestration'
  selectedProject: null,
  selectedCollection: null,
  selectedRequest: null,
  isLoading: false,
};

/**
 * Workspace slice for managing workspace/API builder state
 */
const workspaceSlice = createSlice({
  name: 'workspace',
  initialState,
  reducers: {
    /**
     * Set the active tab (API or Orchestration)
     * @param {Object} state - Current state
     * @param {Object} action - Action with payload: 'API' | 'Orchestration'
     */
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },

    /**
     * Set the selected project
     * @param {Object} state - Current state
     * @param {Object} action - Action with payload: project object or null
     */
    setSelectedProject: (state, action) => {
      state.selectedProject = action.payload;
      // Reset collection and request when project changes
      state.selectedCollection = null;
      state.selectedRequest = null;
    },

    /**
     * Set the selected collection
     * @param {Object} state - Current state
     * @param {Object} action - Action with payload: collection object or null
     */
    setSelectedCollection: (state, action) => {
      state.selectedCollection = action.payload;
      // Reset request when collection changes
      state.selectedRequest = null;
    },

    /**
     * Set the selected request
     * @param {Object} state - Current state
     * @param {Object} action - Action with payload: request object or null
     */
    setSelectedRequest: (state, action) => {
      state.selectedRequest = action.payload;
    },

    /**
     * Set the loading state
     * @param {Object} state - Current state
     * @param {Object} action - Action with payload: boolean
     */
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },

    /**
     * Reset workspace state to initial values
     * @param {Object} state - Current state
     */
    resetWorkspace: (state) => {
      state.activeTab = initialState.activeTab;
      state.selectedProject = null;
      state.selectedCollection = null;
      state.selectedRequest = null;
      state.isLoading = false;
    },
  },
});

// Export actions
export const {
  setActiveTab,
  setSelectedProject,
  setSelectedCollection,
  setSelectedRequest,
  setLoading,
  resetWorkspace,
} = workspaceSlice.actions;

// Selectors
export const selectActiveTab = (state) => state.workspace.activeTab;
export const selectSelectedProject = (state) => state.workspace.selectedProject;
export const selectSelectedCollection = (state) => state.workspace.selectedCollection;
export const selectSelectedRequest = (state) => state.workspace.selectedRequest;
export const selectIsLoading = (state) => state.workspace.isLoading;

// Export reducer
export default workspaceSlice.reducer;
